/**
 * CUT-8(c): the per-field history affordance on the record panel — a small "history" button that
 * expands into the cell's append-only version stream (value · who · when · how), newest first.
 *
 * Without it §3.8's stream is as invisible as no history at all, which is why the spec makes the
 * reader part of the slice rather than a follow-on.
 *
 * Two deliberate choices:
 *
 *  - **Lazy.** The query stays disabled until the operator opens it, so opening a record never fans
 *    out one request per field. History is a question you ask about one cell, not a page load.
 *  - **Not offered on fact-projected columns.** Those belong to the relationship ledger, which
 *    carries its own assert/supersede/retract trail — one authority per field, never two
 *    (spec §3.8). The panel wires this into the EAV-backed rows only.
 *
 * `divergence` is rendered rather than smoothed over: after a rollback to Affinity the import
 * overwrites the cell and correctly appends nothing, so the stream head can legitimately disagree
 * with what the grid shows. Saying so is the point — that is exactly the window in which someone is
 * trying to reconstruct what happened.
 */
import { useState } from 'react';

import { useCrmFieldHistory } from '../../hooks/useCrm';
import type { CrmFieldHistoryEntry } from '../../types';
import styles from './crm.module.css';

// Date AND minute — three same-day edits rendered as three identical "2026-08-18" rows, which made
// the stream unreadable exactly when it mattered (owner report, 2026-08-18).
const fmtWhen = (iso: string | null): string => (iso ? iso.slice(0, 16).replace('T', ' ') : '');

/** The stream stores whatever the cell held — scalar, list, or an entity-ref object. */
const fmtValue = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '(empty)';
  if (Array.isArray(v)) {
    return v.length === 0 ? '(empty)' : v.map(fmtValue).join(', ');
  }
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.name === 'string' && o.name) return o.name;
    return JSON.stringify(v);
  }
  return String(v);
};

/** `actor` is an email for a human edit and a producer identity for a machine one (the E2f-5 rule
 *  means a machine action is never attributed to a person, so this never invents a name). */
const fmtActor = (actor: string | null): string => actor || 'automatic';

export function CrmFieldHistory({ listId, entryId, fieldKey }: {
  listId: number | null;
  entryId: number | null;
  fieldKey: string;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading, isError } = useCrmFieldHistory(listId, entryId, fieldKey, open);

  return (
    <span className={styles.fieldHistoryWrap}>
      <button
        type="button"
        className={styles.fieldHistoryBtn}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Change history for ${fieldKey}`}
        title="Change history"
      >
        ⟲
      </button>
      {open && (
        <div className={styles.fieldHistoryPop} role="group" aria-label={`${fieldKey} history`}>
          {isLoading && <div className={styles.fieldHistoryNote}>Loading…</div>}
          {isError && <div className={styles.fieldHistoryNote}>Couldn’t load history.</div>}
          {data && data.entries.length === 0 && (
            <div className={styles.fieldHistoryNote}>No recorded changes yet.</div>
          )}
          {data && data.divergence && (
            <div className={styles.fieldHistoryDiverge}>
              The current value ({fmtValue(data.divergence.current)}) was not set through the
              product, so it is not in this history — it came from an import.
            </div>
          )}
          {data && data.entries.length > 0 && (
            <ol className={styles.fieldHistoryList}>
              {data.entries.map((e: CrmFieldHistoryEntry) => (
                <li key={e.version} className={styles.fieldHistoryItem}>
                  <span className={styles.fieldHistoryValue}>{fmtValue(e.value)}</span>
                  <span className={styles.fieldHistoryMeta}>
                    {fmtActor(e.actor)}
                    {e.occurred_at ? ` · ${fmtWhen(e.occurred_at)}` : ''}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </span>
  );
}
